// @flow

import type {
  actionTypeNestedSliceType,
  actionTypeSingletonSliceType,
  actionTypeSliceType,
} from './dataStoreTypes';
import type { wrappedApiResponseType } from '../api/apiTypes';

// ----------------
//   Create Types
// ----------------

const asyncTypes = {
  IFNEEDED: 'IFNEEDED', // A softer request, preferring cache over remote data.
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  SHORTDIRECT: 'SHORTDIRECT',
};

export const createTypes = (typePrefixString: string, subTypes: Object) => ({
  ...subTypes, // This is just to satisfy the type checker.  These are overwritten.
  ...Object.keys(subTypes)
    .map(e => subTypes[e])
    .reduce(
      (acc, curr) => ({
        ...acc,
        [curr]: `${typePrefixString}_${curr}`,
      }),
      {},
    ),
});

export const createAsyncTypes = (typePrefixString: string) =>
  createTypes(typePrefixString, asyncTypes);

export const createSingleAndManyAsyncTypes = (typePrefixString: string) => ({
  SINGLE: createAsyncTypes(`${typePrefixString}_SINGLE`),
  MANY: createAsyncTypes(`${typePrefixString}_MANY`),
});

export const createSingletonAsyncTypes = (typePrefixString: string) => ({
  SINGLETON: createAsyncTypes(`${typePrefixString}_SINGLETON`),
});

export const createNestedAsyncTypes = (typePrefixString: string) =>
  createSingleAndManyAsyncTypes(typePrefixString);

// --------------------------
//   Create Action Creators
// --------------------------

export const createAction = (type: string, payload: Object = {}) => ({
  type,
  ...payload,
});

export const createActionBundleForSingle = (
  typeBundle: actionTypeSliceType,
) => ({
  ifNeeded: (id: number) => createAction(typeBundle.SINGLE.IFNEEDED, { id }),
  pending: (id: number) => createAction(typeBundle.SINGLE.PENDING, { id }),
  success: (id: number, ids: Array<number>, entities: any) =>
    createAction(typeBundle.SINGLE.SUCCESS, { id, ids, entities }),
  error: (id: number, status: number, errors: any) =>
    createAction(typeBundle.SINGLE.ERROR, { id, status, errors }),
  shortDirect: (id: number, apiResponse: wrappedApiResponseType<*>) =>
    createAction(typeBundle.SINGLE.SHORTDIRECT, { id, apiResponse }),
});

export const createActionBundleForMany = (typeBundle: actionTypeSliceType) => ({
  ifNeeded: () => createAction(typeBundle.MANY.IFNEEDED),
  pending: () => createAction(typeBundle.MANY.PENDING),
  success: (ids: Array<number>, entities: any, sessionId: number) =>
    createAction(typeBundle.MANY.SUCCESS, {
      ids,
      entities,
      sessionId,
    }),
  error: (status: number, errors: any) =>
    createAction(typeBundle.MANY.ERROR, { status, errors }),
  shortDirect: (apiResponse: wrappedApiResponseType<*>) =>
    createAction(typeBundle.MANY.SHORTDIRECT, { apiResponse }),
});

export const createActionBundleForSingleton = (
  typeBundle: actionTypeSingletonSliceType,
) => ({
  ifNeeded: () => createAction(typeBundle.SINGLETON.IFNEEDED),
  pending: () => createAction(typeBundle.SINGLETON.PENDING),
  success: (id: Array<number>, entities: any) =>
    createAction(typeBundle.SINGLETON.SUCCESS, {
      id,
      entities,
    }),
  error: (status: number, errors: any) =>
    createAction(typeBundle.SINGLETON.ERROR, { status, errors }),
  shortDirect: (apiResponse: wrappedApiResponseType<*>) =>
    createAction(typeBundle.SINGLETON.SHORTDIRECT, { apiResponse }),
});

export const createActionBundleForNestedSingle = (
  typeBundle: actionTypeNestedSliceType,
) => ({
  ifNeeded: (parentId: number, id: number) =>
    createAction(typeBundle.SINGLE.IFNEEDED, { parentId, id }),
  pending: (parentId: number, id: number) =>
    createAction(typeBundle.SINGLE.PENDING, { parentId, id }),
  success: (parentId: number, id: number, ids: Array<number>, entities: any) =>
    createAction(typeBundle.SINGLE.SUCCESS, { parentId, id, ids, entities }),
  error: (parentId: number, id: number, status: number, errors: any) =>
    createAction(typeBundle.SINGLE.ERROR, { parentId, id, status, errors }),
  shortDirect: (
    parentId: number,
    id: number,
    apiResponse: wrappedApiResponseType<*>,
  ) =>
    createAction(typeBundle.SINGLE.SHORTDIRECT, { parentId, id, apiResponse }),
});

export const createActionBundleForNestedMany = (
  typeBundle: actionTypeNestedSliceType,
) => ({
  ifNeeded: (parentId: number) =>
    createAction(typeBundle.MANY.IFNEEDED, { parentId }),
  pending: (parentId: number) =>
    createAction(typeBundle.MANY.PENDING, { parentId }),
  success: (
    parentId: number,
    ids: Array<number>,
    entities: any,
    sessionId: number,
  ) =>
    createAction(typeBundle.MANY.SUCCESS, {
      parentId,
      ids,
      entities,
      sessionId,
    }),
  error: (parentId: number, status: number, errors: any) =>
    createAction(typeBundle.MANY.ERROR, { parentId, status, errors }),
  shortDirect: (parentId: number, apiResponse: wrappedApiResponseType<*>) =>
    createAction(typeBundle.MANY.SHORTDIRECT, { parentId, apiResponse }),
});

export const mapEntitiesToHaveSessionId = (sessionId: number, entities: {}) =>
  // $FlowFixMe
  Object.fromEntries(
    Object.entries(entities).map(([key, value]) => [
      key,
      Object.fromEntries(
        // $FlowFixMe
        Object.entries(value).map(([keyX, valueX]) => [
          keyX,
          { ...valueX, sessionId },
        ]),
      ),
    ]),
  );
